import { keyName } from "../../ui/input/router.ts";

/**
 * The keys no action can be bound to (P1.9-T50; ADR-061's amendments "key rebinding", decision 5, and "every key
 * rebindable but Escape", point 5), in one place, each with why. A physical key is named by `KeyboardEvent.code`, and a
 * key by `KeyboardEvent.key`.
 */

/**
 * Why a key cannot be bound: `menu` is Escape, the game menu's and every layer's close; `browser` is a function key the
 * browser keeps; `system` is a lock key, a media key or one the system keeps; `unnamed` is a key the browser cannot
 * name.
 */
export type ReservedReason = "menu" | "browser" | "system" | "unnamed";

/**
 * The function keys past F12, which few keyboards have and systems map as they please.
 */
const HIGH_FUNCTION_KEYS: readonly string[] = Array.from({ length: 12 }, (_, index) => `F${index + 13}`);

/**
 * Each reason's keys, each by its code, which for these is also the name it types.
 */
const BY_REASON: Readonly<Record<ReservedReason, readonly string[]>> = {
  menu: ["Escape"],
  browser: ["F1", "F3", "F5", "F6", "F7", "F10", "F11", "F12"],
  system: ["CapsLock", "NumLock", "ScrollLock", "PrintScreen", "Pause", "ContextMenu", "OS", "Power", "Sleep", "WakeUp", "BrowserBack", "BrowserForward", "BrowserRefresh", "BrowserHome", "BrowserSearch", "BrowserFavorites", "BrowserStop", "MediaPlayPause", "MediaPlay", "MediaPause", "MediaStop", "MediaTrackNext", "MediaTrackPrevious", "MediaSelect", "LaunchApp1", "LaunchApp2", "LaunchMail", "GoBack", "GoHome", "AppSwitch", "AudioVolumeUp", "AudioVolumeDown", "AudioVolumeMute", ...HIGH_FUNCTION_KEYS],
  unnamed: ["Dead", "Unidentified", "Process", "Compose"],
};

/**
 * Every key that cannot be bound, with why.
 */
export const RESERVED_KEYS: Readonly<Record<string, ReservedReason>> = Object.freeze(Object.fromEntries((Object.entries(BY_REASON) as [ReservedReason, readonly string[]][]).flatMap(([reason, keys]) => keys.map((key) => [key, reason]))));

/**
 * Why a key, by the name it types, cannot be bound.
 *
 * @param key The `KeyboardEvent.key` value; a letter matches in either case.
 * @returns The reason, or null for a key an action may hold.
 * @example
 * reservedReason("Escape");
 * // "menu"
 */
export function reservedReason(key: string): ReservedReason | null {
  const name = keyName(key);
  return Object.hasOwn(RESERVED_KEYS, name) ? (RESERVED_KEYS[name] as ReservedReason) : null;
}

/**
 * Why a physical key cannot be bound.
 *
 * @param code The `KeyboardEvent.code` value.
 * @returns The reason, or null for a physical key an action may hold.
 * @example
 * reservedCode("F5");
 * // "browser"
 */
export function reservedCode(code: string): ReservedReason | null {
  return Object.hasOwn(RESERVED_KEYS, code) ? (RESERVED_KEYS[code] as ReservedReason) : null;
}
